"use client"

import * as React from "react"
import { Input } from "@/components/ui/input"
import { useFormatters } from "@/hooks/use-formatters"
import { useNumericInput } from "@/hooks/use-numeric-input"

export interface DecimalInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'readOnly'> {
  /** The numeric value */
  value?: number | null
  /** Called when the numeric value changes */
  onChange?: (value: number | null) => void
  /** Number of decimal places to format and round to (default: 2) */
  decimalPlaces?: number
  /** Minimum decimal places to show (default: 0 for flexible display) */
  minDecimalPlaces?: number
  /** Whether to allow negative values */
  allowNegative?: boolean
  /** Whether the input is read-only */
  readOnly?: boolean
}

// Decimal input component for precise numeric values
const DecimalInput = React.forwardRef<HTMLInputElement, DecimalInputProps>(
  ({ className, value, onChange, decimalPlaces = 2, minDecimalPlaces = 0, allowNegative = false, readOnly = false, ...props }, ref) => {
    const { formatDecimal } = useFormatters()
    
    // Use shared numeric input logic
    const {
      displayValue,
      handleFocus,
      handleBlur,
      handleChange,
      handleKeyDown
    } = useNumericInput({
      value,
      decimalPlaces,
      allowNegative,
      formatter: (val) => formatDecimal(val, decimalPlaces, minDecimalPlaces)
    })

    return (
      <Input
        type="text"
        className={className}
        ref={ref}
        value={displayValue}
        readOnly={readOnly}
        {...props}
        {...(readOnly ? {} : {
          onChange: handleChange,
          onFocus: (e) => handleFocus(e, value, props.onFocus),
          onBlur: (e) => handleBlur(e, displayValue, onChange, props.onBlur),
          onKeyDown: (e) => handleKeyDown(e, props.onKeyDown)
        })}
      />
    )
  }
)
DecimalInput.displayName = "DecimalInput"

export { DecimalInput }
